import React from 'react';
import {
  Button,
  Input,
  Form,
  Dropdown,
} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import uuidv1 from 'uuid/v1';

import PortalApi from '../../../services/PortalApi';
import Utils from '../../../lib/utils';

class ProgramProfessions extends React.PureComponent {
  state = {
    professionOptions: [],

    professionAssignments: this.props.professionAssignments.map(assignment => ({
      profession_id: assignment.profession_id,
      seq: assignment.seq,
      type: assignment.type,
      uuid: uuidv1(),
    })),

    isSaving: false,
    isPrefillingProfessions: false,
  }

  async componentDidMount() {
    await this.loadProfessions();
  }

  loadProfessions = async () => {
    const professions = await PortalApi.admin.getProfessions();
    this.setState({
      professionOptions: professions.map(profession => ({
        key: profession.id,
        value: profession.id,
        text: profession.name,
      })),
    });
  }

  handleProfessionAssignmentChange = (uuid, key, value) => {
    this.setState(prevState => ({
      professionAssignments: prevState.professionAssignments.map((assignment) => {
        if (assignment.uuid === uuid) {
          return {
            ...assignment,
            [key]: value,
          };
        }
        return { ...assignment };
      }),
    }));
  }

  removeItem = (uuid) => {
    this.setState(prevState => ({
      professionAssignments: prevState.professionAssignments
        .filter(assignment => assignment.uuid !== uuid),
    }));
  }

  addItem = (type) => {
    this.setState(prevState => ({
      professionAssignments: [
        ...prevState.professionAssignments,
        {
          profession_id: null,
          seq: 9999,
          uuid: uuidv1(),
          type,
        },
      ],
    }));
  }

  save = async () => {
    const body = this.state.professionAssignments.map(assignment => ({
      profession_id: assignment.profession_id,
      seq: assignment.seq,
      type: assignment.type,
    }));

    const isValid = body.every(assignment => !!assignment.profession_id && !!assignment.seq);
    if (!isValid) {
      Utils.toastAdminGeneralError(new Error('Profession and Sequence are required'));
      return;
    }

    this.setState({
      isSaving: true,
    });

    try {
      await PortalApi.admin.saveProgramProfessionAssignments(this.props.programId, body);
      Utils.toastAdminGeneralSuccess('Saved Professions!');
    } catch (error) {
      if (error instanceof PortalApi.ApiError) {
        Utils.toastAdminApiError(error);
      } else {
        Utils.toastAdminGeneralError(error);
      }
    }

    this.setState({
      isSaving: false,
    });
  }

  renderProfessionAssignments = (type) => {
    const components = this.state.professionAssignments
      .filter(assignment => assignment.type === type)
      .map(assignment => (
        <Form.Group key={assignment.uuid}>
          <Form.Field
            control={Dropdown}
            inline
            width={4}
            fluid
            options={this.state.professionOptions}
            placeholder="Profession"
            selection
            required
            lazyLoad
            search
            value={assignment.profession_id}
            onChange={(_, { value }) => { this.handleProfessionAssignmentChange(assignment.uuid, 'profession_id', value); }}
          />
          <Form.Field
            control={Input}
            inline
            width={2}
            defaultValue={assignment.seq}
            required
            placeholder="Sequence (Ordering)"
            type="number"
            onChange={(_, { value }) => { this.handleProfessionAssignmentChange(assignment.uuid, 'seq', value); }}
          />
          <Button
            negative
            onClick={() => { this.removeItem(assignment.uuid); }}
            type="button"
            content="Remove"
          />
        </Form.Group>
      ));

    return (
      <div>
        { components }
        <Button
          positive
          onClick={() => { this.addItem(type); }}
          type="button"
          content="Add More"
        />
      </div>
    );
  }

  /* Get template professions from selected disciplines and prefill the options */
  prefillProfessions = async () => {
    this.setState({
      isPrefillingProfessions: true,
    });
    const professions = await PortalApi.admin.getDisciplineProfessions({
      disciplineIds: this.props.programDisciplines.map(discipline => discipline.id),
    });
    const newProfessionAssignments = [];
    // merge with prevState.professionAssignments
    professions.forEach((p) => {
      const idx = this.state.professionAssignments.findIndex((obj) => {
        return obj.profession_id === p.profession_id && obj.type === p.type;
      });
      if (idx === -1) {
        newProfessionAssignments.push({
          ...p,
          seq: 9999,
          uuid: uuidv1(),
        });
      }
    });
    this.setState(prevState => ({
      professionAssignments: [
        ...prevState.professionAssignments,
        ...newProfessionAssignments,
      ],
    }));
    this.setState({
      isPrefillingProfessions: false,
    });
  }

  render() {
    return (
      <Form>
        <h1>Professions</h1>
        <Button
          content="Load professions from disciplines"
          type="submit"
          onClick={this.prefillProfessions}
          loading={this.state.isPrefillingProfessions}
        />
        <h3>Default Professions</h3>
        { this.renderProfessionAssignments('default') }

        <h3>Professions serving Related Sectors</h3>
        { this.renderProfessionAssignments('faculty') }
        <Button
          style={{ marginTop: '10px' }}
          content="Save"
          type="submit"
          onClick={this.save}
          loading={this.state.isSaving}
        />
      </Form>
    );
  }
}

ProgramProfessions.propTypes = {
  programId: PropTypes.number.isRequired,

  professionAssignments: PropTypes.arrayOf(PropTypes.shape({
    profession_id: PropTypes.number.isRequired,
    seq: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired).isRequired,

  programDisciplines: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired).isRequired,
};

export default ProgramProfessions;
